import {
  memoizedLoginViewModel,
  createLoginViewModel,
  LoginViewModel,
} from '../loginViewModel/loginViewModel';
import { ViewModelFactoryParams } from '../../../../utilsDeprecated/ControlledComponent/ControlledComponent.types';
import { CalendarStateDeprecated } from '../../controller';
import { CalendarContext } from '../../../../utilsDeprecated/context/contextFactory';
import { MemoizedViewModalFactory } from '../viewModel';
import {
  BookingDetailsViewModel,
  createBookingDetailsViewModel,
  createDummyBookingDetailsViewModel,
  memoizedBookingDetailsViewModel,
} from '../bookingDetailsViewModel/bookingDetailsViewModel';
import {
  isDailyAgendaWeeklyPickerLayout,
  isWeeklyTimetableLayout,
} from '../../../../utilsDeprecated/layouts';
import { Optional } from '../../../../types/types';
import {
  CtaViewModel,
  createCtaViewModel,
  createDummyCtaViewModel,
  memoizedCtaViewModel,
} from '../ctaViewModel/ctaViewModel';
import {
  SummaryViewModel,
  createSummaryViewModel,
  memoizedSummaryViewModel,
} from '../summaryViewModel/summaryViewModel';

export type SidebarViewModel = {
  title: string;
  bookingDetailsViewModel?: BookingDetailsViewModel;
  summaryViewModel?: SummaryViewModel;
  ctaViewModel: CtaViewModel;
  loginViewModel?: LoginViewModel;
};

export const memoizedSidebarViewModel: MemoizedViewModalFactory<
  Optional<SidebarViewModel>
> = {
  dependencies: {
    settings: ['bookingDetailsSectionHeader', 'calendarLayout'],
    subDependencies: [
      memoizedBookingDetailsViewModel.dependencies,
      memoizedLoginViewModel.dependencies,
      memoizedSummaryViewModel.dependencies,
      memoizedCtaViewModel.dependencies,
    ],
  },
  createViewModel: createSidebarViewModel,
};

export function createSidebarViewModel({
  state,
  context,
}: ViewModelFactoryParams<
  CalendarStateDeprecated,
  CalendarContext
>): Optional<SidebarViewModel> {
  const { getContent, settings, settingsParams, isDateAndTimeViewMode } =
    context;
  const { selectableSlotsAtSelectedTime } = state;

  const isTimetableWidget =
    isDailyAgendaWeeklyPickerLayout(settings, settingsParams) ||
    isWeeklyTimetableLayout(settings, settingsParams);
  if (isTimetableWidget) {
    return;
  }

  return {
    title: getContent({
      settingsParam: settingsParams.bookingDetailsSectionHeader,
      translationKey: isDateAndTimeViewMode
        ? 'app.settings.defaults.widget.appointment-details-header'
        : 'app.settings.defaults.widget.booking-details-header',
    }),
    bookingDetailsViewModel: !isDateAndTimeViewMode
      ? createBookingDetailsViewModel({ state, context })
      : undefined,
    summaryViewModel: isDateAndTimeViewMode
      ? createSummaryViewModel({ state, context })
      : undefined,
    loginViewModel: createLoginViewModel({ state, context }),
    ctaViewModel: createCtaViewModel({
      state,
      context,
      slots: selectableSlotsAtSelectedTime || [],
    }),
  };
}

export const createDummySidebarViewModel = ({
  state,
  context,
}: {
  state: CalendarStateDeprecated;
  context: CalendarContext;
}): SidebarViewModel => {
  const { getContent, settingsParams } = context;

  return {
    title: getContent({
      settingsParam: settingsParams.bookingDetailsSectionHeader,
      translationKey: 'app.settings.defaults.widget.booking-details-header',
    }),
    loginViewModel: createLoginViewModel({ state, context }),
    bookingDetailsViewModel: createDummyBookingDetailsViewModel({
      state,
      context,
    }),
    ctaViewModel: createDummyCtaViewModel({ state, context }),
  };
};
